import React, { useState } from 'react';
import { updateProfile } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebaseInit';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { useNotification } from '../contexts/NotificationContext';
import { normalizeEmailToDocId } from '../utils/emailNormalization';
import { getUserPath } from '../utils/firestorePaths';
import { getArtifactProjectId } from '../firebaseInit';
import Button from './shared/Button';

/**
 * ProfileCompletionPrompt
 * 
 * Shows a non-blocking banner for existing users who don't have
 * firstName/lastName in their user document. Allows them to add
 * their name, which gets saved to Firestore + Firebase Auth displayName.
 * 
 * Displayed once per session until the user completes or dismisses it.
 * Won't show again if profileCompleted is true on the user document.
 * 
 * Uses userDefaults from DataContext when available, falls back to
 * Firebase Auth displayName when userDefaults hasn't loaded yet.
 */
export default function ProfileCompletionPrompt() {
  const { isAuthenticated, user, claims } = useAuth();
  const { userDefaults, refreshUserDefaults } = useData();
  const { showSuccess, showError } = useNotification();

  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('profile_prompt_dismissed') === 'true');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);

  // Don't show if not authenticated
  if (!isAuthenticated) return null;

  // If userDefaults is loaded, use it as the source of truth
  if (userDefaults) {
    // User already has both names — no need to prompt
    if (userDefaults.firstName && userDefaults.lastName) return null;
    // Profile explicitly marked as completed
    if (userDefaults.profileCompleted) return null;
  } else {
    // userDefaults hasn't loaded (e.g. claims.teamId not yet available).
    // Fall back to Firebase Auth displayName to avoid hiding the banner
    // for users who genuinely need it, while also not flashing it for
    // users who already have their name set.
    const displayName = user?.displayName || '';
    const parts = displayName.trim().split(/\s+/);
    // If displayName has at least two words, assume profile is complete
    if (parts.length >= 2 && parts[0] && parts[1]) return null;
  }

  // Don't show if dismissed this session
  if (dismissed) return null;

  const handleSave = async () => {
    const trimmedFirst = firstName.trim().substring(0, 50);
    const trimmedLast = lastName.trim().substring(0, 50);

    if (!trimmedFirst && !trimmedLast) {
      showError('Please enter at least your first name');
      return;
    }

    setSaving(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser?.email) {
        throw new Error('Not authenticated');
      }

      const teamId = user?.claims?.teamId || claims?.teamId;
      if (!teamId) {
        // Try getting teamId from the auth token
        const tokenResult = await currentUser.getIdTokenResult();
        const claimsTeamId = tokenResult.claims?.teamId;
        if (!claimsTeamId) {
          throw new Error('No team found. Please contact your team lead.');
        }
        // Use the claims teamId
        await saveProfile(currentUser, trimmedFirst, trimmedLast, claimsTeamId);
      } else {
        await saveProfile(currentUser, trimmedFirst, trimmedLast, teamId);
      }

      showSuccess('Profile updated! Your name will now appear on the leaderboard and team views.');
      
      // Refresh user defaults to pick up the new name
      if (refreshUserDefaults) {
        await refreshUserDefaults();
      }
      
      setDismissed(true);
    } catch (error) {
      console.error('Error updating profile:', error);
      showError('Could not update your profile. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = async (currentUser, trimmedFirst, trimmedLast, teamId) => {
    const emailDocId = normalizeEmailToDocId(currentUser.email);
    const appId = getArtifactProjectId();
    const userPath = getUserPath(appId, teamId, emailDocId);

    // Update Firebase Auth displayName first (doesn't depend on Firestore doc existing)
    const displayName = [trimmedFirst, trimmedLast].filter(Boolean).join(' ');
    if (displayName) {
      await updateProfile(currentUser, { displayName });
    }

    // Update Firestore user document (merge: true creates if missing)
    const userDocRef = doc(db, userPath);
    const updates = {
      profileCompleted: true,
      updatedAt: new Date(),
    };
    if (trimmedFirst) updates.firstName = trimmedFirst;
    if (trimmedLast) updates.lastName = trimmedLast;

    await setDoc(userDocRef, updates, { merge: true });
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
      <div className="flex items-start gap-3">
        <div className="text-blue-600 text-lg flex-shrink-0">👋</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-blue-800 mb-2">
            Welcome back! Add your name so your team knows who you are.
          </p>
          <div className="flex items-center gap-2 mb-2">
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name"
              className="flex-1 px-2 py-1.5 text-sm border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={saving}
            />
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last name"
              className="flex-1 px-2 py-1.5 text-sm border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={saving}
            />
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              loading={saving}
              disabled={saving}
            >
              Save
            </Button>
          </div>
          <button
            onClick={() => { setDismissed(true); sessionStorage.setItem('profile_prompt_dismissed', 'true'); }}
            className="text-xs text-blue-500 hover:text-blue-700 underline"
            disabled={saving}
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
}
